import React, { useState, useEffect } from "react";
import {
    DataGrid,
    GridFooterContainer,
    GridPagination,
    GridRenderCellParams,
    GridSortModel
} from "@mui/x-data-grid";
import CustomColumnHeader from "features/leadOverview/components/CustomTableHeader";
import LeadOverviewFilters from "features/leadOverview/components/LeadOverviewFilters";
import TaskDetailModal from "./TaskDetailModal"; // Import des Modals
import "./TaskManagement.scss"; // Styling

export interface Task {
    id: string;
    title: string;
    status: "todo" | "inProgress" | "done" | "lost";
    priority: "low" | "medium" | "high";
    category: "Kurzzeitpflege" | "Vollstationäre Pflege" | "Pflegedienst" | "Immobilienverrentung" | "Tagespflege" | "24 Stunden Betreuung" | "Betreutes Wohnen" | "Badsanierung" | "Hospiz" | "Sitzlift"
    dueDate?: string;
    description?: string;
    assignedTo?: string;
    // Zusätzliche Felder aus den PatientMockData
    product?: string;
    location?: string;
    dealAmount?: number;
    subtasks?: { id: string; title: string; completed: boolean }[];
}

// MockData für Tasks exakt aus PatientMockData
const mockTasks: Task[] = [
    {
        id: "101",
        title: "Max Mustermann",
        status: "inProgress",
        priority: "high",
        category: "Pflegedienst",
        dueDate: "2025-04-07",
        description: "Termin vorbereiten",
        assignedTo: "Peter Meier",
        product: "Pflegegrad 3",
        location: "Berlin",
        dealAmount: 1500,
        subtasks: [
            { id: "1", title: "Klient anrufen", completed: true },
            { id: "2", title: "Termin vereinbaren", completed: true },
            { id: "3", title: "Termin vorbereiten", completed: false }
        ]
    },
    {
        id: "102",
        title: "Peter Schneider",
        status: "todo",
        priority: "high",
        category: "Immobilienverrentung",
        dueDate: "2025-04-11",
        description: "Klient anrufen",
        assignedTo: "Peter Meier",
        product: "Pflegegrad 2",
        location: "Stuttgart",
        dealAmount: 2500,
        subtasks: [
            { id: "1", title: "Klient anrufen", completed: false },
            { id: "2", title: "Termin vereinbaren", completed: false },
            { id: "3", title: "Termin vorbereiten", completed: false }
        ]
    },
    {
        id: "103",
        title: "Lukas Schmidt",
        status: "inProgress",
        priority: "medium",
        category: "Tagespflege",
        dueDate: "2025-03-31",
        description: "Termin vorbereiten",
        assignedTo: "Petra Schmidt",
        product: "Pflegegrad 1",
        location: "Dortmund",
        dealAmount: 1800,
        subtasks: [
            { id: "1", title: "Klient anrufen", completed: true },
            { id: "2", title: "Termin vereinbaren", completed: true },
            { id: "3", title: "Termin vorbereiten", completed: false }
        ]
    },
    {
        id: "104",
        title: "Hans Müller",
        status: "todo",
        priority: "medium",
        category: "24 Stunden Betreuung",
        dueDate: "2025-04-13",
        description: "Klient anrufen",
        assignedTo: "Petra Schmidt",
        product: "Pflegegrad 2",
        location: "München",
        dealAmount: 2000,
        subtasks: [
            { id: "1", title: "Klient anrufen", completed: false },
            { id: "2", title: "Termin vereinbaren", completed: false },
            { id: "3", title: "Termin vorbereiten", completed: false }
        ]
    },
    {
        id: "106",
        title: "Thomas Weber",
        status: "inProgress",
        priority: "medium",
        category: "Badsanierung",
        dueDate: "2025-04-20",
        description: "Vertrag zusenden",
        assignedTo: "Hans Schmidt",
        product: "Pflegegrad 1",
        location: "Frankfurt",
        dealAmount: 2800,
        subtasks: [
            { id: "1", title: "Klient anrufen", completed: true },
            { id: "2", title: "Termin vereinbaren", completed: true },
            { id: "3", title: "Termin vorbereiten", completed: true }
        ]
    },
    {
        id: "108",
        title: "Anna Schmidt",
        status: "inProgress",
        priority: "medium",
        category: "Sitzlift",
        dueDate: "2025-04-30",
        description: "Klient anrufen",
        assignedTo: "Hans Schmidt",
        product: "Pflegegrad 3",
        location: "Stuttgart",
        dealAmount: 2600,
        subtasks: [
            { id: "1", title: "Klient anrufen", completed: true },
            { id: "2", title: "Termin vereinbaren", completed: true },
            { id: "3", title: "Termin vorbereiten", completed: true }
        ]
    },
    {
        id: "107",
        title: "Julia Schneider",
        status: "inProgress",
        priority: "low",
        category: "Hospiz",
        dueDate: "2025-04-25",
        description: "Zufriedenheit abfragen",
        assignedTo: "Maria Müller",
        product: "Pflegegrad 2",
        location: "Berlin",
        dealAmount: 1700,
        subtasks: [
            { id: "1", title: "Klient anrufen", completed: true },
            { id: "2", title: "Termin vereinbaren", completed: true },
            { id: "3", title: "Termin vorbereiten", completed: true }
        ]
    },

    {
        id: "109",
        title: "Sophie Hoffmann",
        status: "done",
        priority: "low",
        category: "Kurzzeitpflege",
        dueDate: "2025-05-05",
        description: "Termin vereinbaren",
        assignedTo: "Hans Schmidt",
        product: "Pflegegrad 1",
        location: "München",
        dealAmount: 3000,
        subtasks: [
            { id: "1", title: "Klient anrufen", completed: true },
            { id: "2", title: "Termin vereinbaren", completed: true },
            { id: "3", title: "Termin vorbereiten", completed: true }
        ]
    },
    {
        id: "110",
        title: "Erika Musterfrau",
        status: "done",
        priority: "low",
        category: "Kurzzeitpflege",
        dueDate: "2025-05-10",
        description: "Termin vorbereiten",
        assignedTo: "Maria Müller",
        product: "Pflegegrad 2",
        location: "Frankfurt",
        dealAmount: 1900,
        subtasks: [
            { id: "1", title: "Klient anrufen", completed: true },
            { id: "2", title: "Termin vereinbaren", completed: true },
            { id: "3", title: "Termin vorbereiten", completed: true }
        ]
    },
    {
        id: "111",
        title: "Maximilian Becker",
        status: "done",
        priority: "low",
        category: "Pflegedienst",
        dueDate: "2025-05-15",
        description: "Klient anrufen",
        assignedTo: "Maria Müller",
        product: "Pflegegrad 3",
        location: "Hamburg",
        dealAmount: 3500,
        subtasks: [
            { id: "1", title: "Klient anrufen", completed: false },
            { id: "2", title: "Termin vereinbaren", completed: false },
            { id: "3", title: "Termin vorbereiten", completed: false }
        ]
    }
];

const TaskOverview = () => {
    const [tasks, setTasks] = useState<Task[]>([]);
    const [pageSize, setPageSize] = useState<number>(10);
    const [page, setPage] = useState<number>(0);
    const [rowCount, setRowCount] = useState<number>(0);
    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [selectedStatusItems, setSelectedStatusItems] = useState<number[]>([]);
    const [selectedLeadTypeItems, setSelectedLeadTypeItems] = useState<number[]>([]);
    const [selectedProductItems, setSelectedProductItems] = useState<number[]>([]);
    const [nameSearchText, setNameSearchText] = useState<string>("");
    const [anfragenNumberSearchText, setAnfragenNumberSearchText] = useState<string>("");

    // Neuer State für das ausgewählte Lead (optional)
    const [isCreateClient, setIsCreateClient] = useState(false);

    // States für das Modal und den ausgewählten Task
    const [isModalOpen, setModalOpen] = useState<boolean>(false);
    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [isSidebarOpen, setSidebarOpen] = useState<boolean>(false);

    // Simuliere den Datenabruf
    useEffect(() => {
        setLoading(true);
        setTimeout(() => {
            setTasks(mockTasks);
            setRowCount(mockTasks.length);
            setLoading(false);
        }, 500);
    }, []);

    const handleSortModelChange = (model: GridSortModel) => {
        setSortModel(model);
    };

    const setIsCreateTask = (isCreateTask: boolean) => {
        setIsCreateClient(isCreateTask);
    };

    const handleNavigate = (id: string, origin: string) => {
        // Navigation implementieren, falls notwendig
        console.log(`Navigiere zu ${origin} mit ID ${id}`);
    };

    // Funktion zum Markieren eines Tasks als erledigt
    const handleCompleteTask = (id: string) => {
        setTasks((prevTasks) =>
            prevTasks.map((task) =>
                task.id === id ? { ...task, status: "done" } : task
            )
        );
    };

    // Funktion, um einen Task hinzuzufügen oder zu aktualisieren
    const addOrUpdateTask = (task: Task) => {
        setTasks((prevTasks) =>
            prevTasks.find((t) => t.id === task.id)
                ? prevTasks.map((t) => (t.id === task.id ? task : t))
                : [...prevTasks, task]
        );
    };

    // Öffnet das Modal für einen neuen Task
    const handleAddTask = () => {
        setSelectedTask(null);
        setModalOpen(true);
    };

    const columns = [
        {
            field: "priority",
            headerName: "Priorität",
            flex: 0.05,
            renderHeader: () => (
                <CustomColumnHeader title="Priorität" field="priority" sortModel={sortModel} />
            ),
            renderCell: (params: GridRenderCellParams<any>) => {
                const priorityValue =
                    params.value === "high" ? 1 : params.value === "medium" ? 2 : 3;
                const priorityClass =
                    params.value === "high"
                        ? "high-priority"
                        : params.value === "medium"
                            ? "medium-priority"
                            : "low-priority";
                return <span className={priorityClass}>{priorityValue}</span>;
            }
        },
        {
            field: "description",
            headerName: "Aufgabe",
            flex: 0.2,
            renderHeader: () => (
                <CustomColumnHeader title="Aufgabe" field="description" sortModel={sortModel} />
            )
        },
        {
            field: "title",
            headerName: "Klient",
            flex: 0.2,
            renderHeader: () => (
                <CustomColumnHeader title="Klient" field="title" sortModel={sortModel} />
            )
        },

        {
            field: "category",
            headerName: "Kategorie",
            flex: 0.2,
            renderHeader: () => (
                <CustomColumnHeader title="Kategorie" field="category" sortModel={sortModel} />
            )
        },
        {
            field: "dueDate",
            headerName: "Fälligkeitsdatum",
            flex: 0.1,
            renderHeader: () => (
                <CustomColumnHeader title="Fälligkeitsdatum" field="dueDate" sortModel={sortModel} />
            ),
            renderCell: (params: GridRenderCellParams<any>) =>
                new Date(params.value).toLocaleDateString("de-DE", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric"
                })
        },
        {
            field: "assignedTo",
            headerName: "Zugewiesen an",
            flex: 0.1,
            renderHeader: () => (
                <CustomColumnHeader title="Zugewiesen an" field="assignedTo" sortModel={sortModel} />
            )
        },
        {
            field: "status",
            headerName: "Status",
            flex: 0.1,
            sortable: false,
            renderHeader: () => (
                <CustomColumnHeader title="Status" field="status" sortModel={sortModel} />
            ),
            renderCell: (params: any) => (
                <span className="status-label">
                    {params.value === "todo" && <span className="status-todo">To Do</span>}
                    {params.value === "inProgress" && <span className="status-in-progress">In Bearbeitung</span>}
                    {params.value === "done" && <span className="status-done">Erledigt</span>}
                    {params.value === "lost" && <span className="status-done">Erledigt</span>}
                </span>
            )
        },
        {
            field: "link",
            headerName: "Erledigen",
            flex: 0.09,
            sortable: false,
            renderHeader: () => (
                <CustomColumnHeader title="Erledigen" field="link" sortModel={sortModel} />
            ),
            renderCell: (params: any) => (
                <button
                    className="to-client"
                    onClick={() => {
                        handleCompleteTask(params.row.id);
                    }}
                >
                    <i className="fa-solid fa-check"></i>
                </button>
            )
        }
    ];
    const handleRowClick = (params: any) => {
        setSelectedTask(params.row as Task);
        setSidebarOpen(true);
    };


    const CustomFooter = () => (
        <GridFooterContainer>
            <GridPagination
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
                labelRowsPerPage="Aufgaben pro Seite:"
                onChange={(event) => {
                    const newPage = parseInt((event.target as HTMLInputElement).value, 10);
                    setPage(newPage);
                }}
            />
        </GridFooterContainer>
    );

    return (
        <div className="detail-layout-2">
            <div className="lead-container">
                <h1 className="text-blue mb-50">Aufgaben verwalten</h1>
                <div className="actions">
                    <button onClick={handleAddTask} className="kanban-switch-green">
                        <i className="fa-solid fa-plus"></i> Aufgabe erstellen
                    </button>
                </div>
                <LeadOverviewFilters
                    setPage={setPage}
                    onStatusChange={(statuses: number[]) => setSelectedStatusItems(statuses)}
                    onLeadTypeChange={() => setSelectedLeadTypeItems([])}
                    onProductChange={() => setSelectedProductItems([])}
                    onNameSearchChange={(searchText: string) => setNameSearchText(searchText)}
                    onAnfragenNumberSearchChange={(searchText: string) => setAnfragenNumberSearchText(searchText)}
                />
                <div className="data-grid-container">
                    <DataGrid
                        rows={tasks}
                        columns={columns}
                        pageSize={pageSize}
                        page={page}
                        rowCount={rowCount}
                        autoHeight
                        loading={loading}
                        sortingOrder={["asc", "desc"]}
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        onPageChange={(newPage) => setPage(newPage)}
                        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                        paginationMode="server"
                        disableSelectionOnClick
                        disableColumnMenu
                        components={{ Footer: CustomFooter }}
                        // CSS-Klasse, um erledigte Tasks durchzustreichen
                        getRowClassName={(params) =>
                            params.row.status === "done" ? "task-row-done" : ""
                        }
                        onRowClick={handleRowClick}
                    />
                </div>
            </div>
            {/* Einbindung des Modals */}
            {isModalOpen && (
                <TaskDetailModal
                    task={selectedTask || undefined}
                    onClose={() => setModalOpen(false)}
                    onSave={(task: Task) => {
                        addOrUpdateTask(task);
                        setModalOpen(false);
                    }}
                />
            )}
            {isSidebarOpen && (
                <TaskDetailModal
                    task={selectedTask || undefined}
                    onClose={() => setSidebarOpen(false)}
                    onSave={(task: Task) => {
                        addOrUpdateTask(task);
                        setModalOpen(false);
                    }}
                />
            )}

        </div>
    );
};

export default TaskOverview;
